import {
    BuildAdUnitRequest,
    buildAdUnitRequest,
    getAdUnitsListRequest,
    NewAdUnitInfo,
    getAdUnitByIdRequest
} from "@common";
import { AppState, currentClientId, ImageAssetAttribute } from "@store";
import { put, select, takeEvery, call } from "redux-saga/effects";
import { SagaHttpRequestError } from "../../../api";
import { campaignCreativesForm } from "../../campaign";
import { BuildAdUnit } from "../actions";
import { assetFormSelector } from "../selector";
import { adUnitParamsSelector } from "../selector/adUnitAssetSelector";
import { enumerateForms } from "./enumerateForms";
import { delay } from "redux-saga";

const BUILD_ADUNIT_JOB_POLL_INTERVAL = 1500;

function* buildAdUnitCore() {
    const appState: AppState = yield select();

    const clientId = currentClientId(appState);
    if (!clientId) {
        return;
    }

    const adUnitParams = adUnitParamsSelector(appState);
    if (!adUnitParams) {
        return;
    }

    const assets = assetFormSelector(appState);
    if (!assets) {
        return;
    }

    const forms = enumerateForms(assets);
    if (forms.some(f => !f.data.isValid)) {
        for (const f of forms) {
            yield put(f.form.actions.validate(f.meta));
        }
        return;
    }

    let hasFormValidationErrors = false;
    // Validate fields
    for (const f of forms) {
        const imagesField: ImageAssetAttribute[] = f.assets.filter(a => a.type === "image" && "min" in a) as any;

        if (f.data.isValid) {
            const errors = imagesField.reduce((errors, field) => {
                if (f.data.isValid) {
                    const value: string[] = f.data.data[field.name];

                    if (!value) {
                        errors[field.name] = ["Value is required."];
                        return errors;
                    }

                    if (field.min && value.length < field.min) {
                        errors[field.name] = [`Please add at least ${field.min} images`];
                        return errors;
                    }

                    if (field.max && value.length > field.max) {
                        errors[field.name] = [`Please add max ${field.max} images`];
                        return errors;
                    }
                }

                return errors;
            }, {});

            if (Object.keys(errors).length > 0) {
                hasFormValidationErrors = true;
                yield put(f.form.actions.setErrors({ fields: errors }));
            }
        }
    }

    if (hasFormValidationErrors) {
        return;
    }

    const adUnitNestedFields = assets.nested.reduce((result, asset) => {
        if (asset.isCollection) {
            return {
                ...result,
                [asset.name]: asset.data.map(i => (i.data.isValid ? i.data.data : {}))
            };
        } else {
            const data = asset.data[0];

            return {
                ...result,
                [asset.name]: data.data.isValid ? data.data.data : {}
            };
        }
    }, {});

    const data: BuildAdUnitRequest = {
        ...adUnitParams,
        ...(assets.main.data.isValid ? assets.main.data.data : {}),
        ...adUnitNestedFields
    };

    try {
        const params = { clientId };
        yield put(buildAdUnitRequest.actions.running(params));

        // console.log("Creating ad unit", data);

        const result: NewAdUnitInfo = (yield call(buildAdUnitRequest, params, data)).result;

        let adunit = result;

        if ("ok" in adunit && !adunit.ok) {
            // console.log("Ad unit creation failed, throwing an error");
            throw new Error("Error buidling Ad Unit");
        }

        // console.log("Ad unit created", adunit);

        while (true) {
            if (adunit.status !== "pending") {
                // console.log("Ad unit created, breaking polling");
                break;
            }

            // console.log("Ad unit creation is in progress, waiting...");

            yield delay(BUILD_ADUNIT_JOB_POLL_INTERVAL);
            adunit = yield getAdUnitByIdRequest.saga({ clientId, unitId: result.id });
            // console.log("Ad unit polled from the server", adunit);
        }

        yield* getAdUnitsListRequest.saga({ clientId });
        yield put(campaignCreativesForm.actions.fieldEdit("adUnitId", result.id, undefined));
        yield put(buildAdUnitRequest.actions.ok(params, { ok: true, result }));
    } catch (e) {
        yield put(buildAdUnitRequest.actions.error({ clientId }, e));
        console.error(e);
        if (e instanceof SagaHttpRequestError) {
            // Handle AD Unit creation error
        }
    }
}

export function* buildAdUnit() {
    yield takeEvery(BuildAdUnit.trigger.type, buildAdUnitCore);
}
